import Email from '@react-spectrum/s2/icons/Email';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };

import type { EffectiveStatus } from '@/hooks/useContentArtifacts';
import { cn } from '@/utils/cn';

import type { Action } from '../../../../bridge/types/action.ts';
import type {
	EmailHtml,
	EmailPlaintext,
} from '../../../../bridge/types/artifact.ts';

import { emailSendDate } from './emailSendDate';
import { EmailThumbnail } from './EmailThumbnail';
import { TileFooter } from './TileFooter';
import { TileSelectionCheckbox } from './TileSelectionCheckbox';

// See DigitalAdAsset: 'gray-700' is not a valid iconStyle color.
const emailIconStyle = iconStyle({ size: 'M', color: 'gray' });

interface EmailAssetProps {
	artifact: EmailHtml | EmailPlaintext;
	action: Action | undefined;
	effectiveStatus: EffectiveStatus;
	onClick?: () => void;
	isSelectable?: boolean;
	isSelected?: boolean;
	onToggleSelect?: () => void;
}

export const EmailAsset = ({
	artifact,
	action,
	effectiveStatus,
	onClick,
	isSelectable,
	isSelected,
	onToggleSelect,
}: EmailAssetProps) => {
	const subject = artifact.content.subject ?? 'Untitled email';
	const date = emailSendDate(artifact, action);

	return (
		<div
			className="group flex cursor-pointer flex-col gap-2"
			data-testid={`content-tile-${artifact.id}`}
			onClick={onClick}
		>
			{/* EmailThumbnail keeps its own `relative` internally, out of reach here,
			    so the checkbox needs a positioning context of its own. */}
			<div className="relative">
				<EmailThumbnail artifact={artifact} size="tile" />
				{isSelectable && onToggleSelect && (
					<TileSelectionCheckbox
						label={subject}
						isSelected={isSelected ?? false}
						onToggle={onToggleSelect}
					/>
				)}
			</div>
			<div className="flex flex-col gap-1">
				<div className="flex justify-between gap-2">
					<div
						className={cn(
							'line-clamp-1 flex-1',
							style({
								font: 'title',
								fontSize: 'title-sm',
								fontWeight: 'bold',
								color: 'title',
							}),
						)}
					>
						{subject}
					</div>
					<Email styles={emailIconStyle} />
				</div>
				<TileFooter status={effectiveStatus} date={date} />
			</div>
		</div>
	);
};
